import React, { useState, useEffect } from 'react';
import { Shield, ExternalLink } from 'lucide-react';

interface PolicyAcceptanceModalProps {
  onAccept: () => Promise<void>;
  isLoading?: boolean;
}

const PolicyAcceptanceModal: React.FC<PolicyAcceptanceModalProps> = ({ onAccept, isLoading = false }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(timer);
  }, []);

  const handleAccept = async () => {
    await onAccept();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full max-w-md transition-all duration-400 ease-out ${
          visible
            ? 'opacity-100 translate-y-0 scale-100'
            : 'opacity-0 translate-y-6 scale-95'
        }`}
      >
        <div className="px-6 pt-7 pb-2 text-center">
          <div className="w-12 h-12 rounded-full bg-[#07E5D1]/15 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-6 h-6 text-[#07E5D1]" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">We value your privacy</h2>
          <p className="text-sm text-gray-500 leading-relaxed">
            Before you continue, please review our policies. By clicking accept, you agree to our{' '}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#07E5D1] hover:text-[#06C9B8] font-medium inline-flex items-center gap-0.5"
            >
              Privacy Policy
              <ExternalLink className="w-3 h-3" />
            </a>{' '}
            and{' '}
            <a
              href="/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#07E5D1] hover:text-[#06C9B8] font-medium inline-flex items-center gap-0.5"
            >
              Terms of Service
              <ExternalLink className="w-3 h-3" />
            </a>
            .
          </p>
        </div>

        <div className="px-6 pt-5 pb-6">
          <button
            onClick={handleAccept}
            disabled={isLoading}
            className={`w-full py-2.5 rounded-lg text-sm font-medium transition-all ${
              isLoading
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-[#07E5D1] text-white hover:bg-[#06D4C2] active:bg-[#05C3B3] shadow-sm hover:shadow'
            }`}
          >
            {isLoading ? 'Processing...' : 'Accept & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PolicyAcceptanceModal;
